import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableRow, TableCell, Checkbox, TextField, Button } from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';

import ItemComboBox, { ComboBoxItemType } from '../../../atoms/ComboBox/ItemComboBox';
import NumericEntry_inputEx from '../../../atoms/Input/NumericEntry_inputEx';
import TimeEntry from '../../../atoms/Input/TimeEntry';

import { V_IOTBOX_SETTING_ITEM } from './IoTBoxSetting';
import { TableUseStyle, IoTBoxSettingStyle } from './IoTBoxSettingStyle';
import { TableBaseStyle } from '../../../../baseStyle/tableBaseStyle';

type IoTBoxSettingGridItemProps = {
  rowno: number;
  selectIdx: number;
  setSelectIdx: Dispatch<SetStateAction<number>>;
  item: V_IOTBOX_SETTING_ITEM;
  updateItem: (item: V_IOTBOX_SETTING_ITEM) => void;
  collectSetting: (item: V_IOTBOX_SETTING_ITEM) => void;
  saveRowAction: (item: V_IOTBOX_SETTING_ITEM) => void;
  lithiumChangeDaytimeAction: (item: V_IOTBOX_SETTING_ITEM) => void;
  devNoLists: ComboBoxItemType[];
  devNameLists: ComboBoxItemType[];
};

/**
 * IoTBox設定 ListRow
 * @param props
 * @returns
 */
const IoTBoxSettingGridItem: React.FC<IoTBoxSettingGridItemProps> = (props: IoTBoxSettingGridItemProps) => {
  const tableBaseStyle = TableUseStyle();
  const pageStyle = IoTBoxSettingStyle();
  const baseClasses = TableBaseStyle();
  const { t } = useTranslation();
  // 行選択ハンドラ
  const selectRow = () => {
    console.debug(['selectRow()', props.rowno]);
    props.setSelectIdx(props.rowno);
  };

  //行選択の背景色一時置き
  let lineSelection = '';

  return (
    <>
      <div className={`${lineSelection = tableBaseStyle.bodyRow} ${lineSelection = baseClasses.bodyRow}`}></div>
      <TableRow
        key={props.rowno + 1}
        hover
        onClick={() => {
          selectRow();
        }}
        selected={props.selectIdx === props.rowno}
        className={lineSelection}
      >
        {/* No */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.rowno + 1}</TableCell>
        {/* 自動収集 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <input
            type="checkbox"
            checked={props.item.RUN_FLG == '1'}
            onChange={(e) => {
              props.item.RUN_FLG = e.target.checked ? '1' : '0';
              props.updateItem(props.item);
            }}
          />
        </TableCell>
        {/* 機器番号 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <ItemComboBox
            itemList={props.devNoLists}
            label=""
            value={props.item.DEV_INDEX}
            onChangeEvent={(e) => {
              //console.debug(`DEV_INDEX:${e.target.value}`);
              if (typeof e.target.value === 'string') {
                props.item.DEV_INDEX = parseInt(e.target.value);
                props.updateItem(props.item);
              }
            }}
          />
        </TableCell>
        {/* 機器名称 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.item.DEV_NAME}</TableCell>
        {/* IMSI */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <Tooltip title={JSON.stringify(props.item)} enterDelay={10000}>
            <div>{props.item.IMSI_NO}</div>
          </Tooltip>
        </TableCell>
        {/* 収集時刻 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <div className={pageStyle.flexBox}>
            <TimeEntry
              value={props.item.LOG_HOUR}
              maxValue={23}
              minValue={0}
              onChange={(val) => {
                props.item.LOG_HOUR = val;
                props.updateItem(props.item);
              }}
              className={tableBaseStyle.logTimeField}
            />
            :
            <TimeEntry
              value={props.item.LOG_MIN}
              maxValue={59}
              minValue={0}
              onChange={(val) => {
                props.item.LOG_MIN = val;
                props.updateItem(props.item);
              }}
              className={tableBaseStyle.logTimeField}
            />
            :
            <TimeEntry
              value={props.item.LOG_SEC}
              maxValue={59}
              minValue={0}
              onChange={(val) => {
                props.item.LOG_SEC = val;
                props.updateItem(props.item);
              }}
              className={tableBaseStyle.logTimeField}
            />
          </div>
        </TableCell>
        {/* 産業用バッテリー残量 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.item.mobileVoltLevel}</TableCell>
        {/* リチウムバッテリ残り日数 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.item.lithiumVoltRemainingDays}</TableCell>
        {/* リチウムバッテリ最大使用日数 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <NumericEntry_inputEx
            value={props.item.LITHIUM_BATTERY ?? 0}
            fixed={0}
            maxValueLimit={9999}
            className={tableBaseStyle.textField}
            //defaultValue={'0'}
            onChange={(val) => {
              console.debug(['onChange()', val]);
              props.item.LITHIUM_BATTERY = val;
              props.updateItem(props.item);
            }}
          ></NumericEntry_inputEx>
        </TableCell>
        {/* リチウムバッテリ交換 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={(_) => {
              props.lithiumChangeDaytimeAction(props.item);
            }}
          >
            {t('IoTBoxSettingGridItem.実行', '実行')}
          </Button>
        </TableCell>
        {/* 設定バージョン */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.item.SET_VER}</TableCell>
        {/* 設定結果 */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>{props.item.IOT_SET_FLG_Disp}</TableCell>
        {/* 収集設定 画面遷移*/}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              props.collectSetting(props.item);
            }}
          >
            {t('IoTBoxSettingGridItem.表示', '表示')}
          </Button>
        </TableCell>
        {/* 設定ファイルDL */}
        <TableCell className={tableBaseStyle.bodyCellCenter}>
          <Button
            variant="outlined"
            color="secondary"
            disabled={!props.item.DL_ENABLE}
            onClick={(e) => {
              props.saveRowAction(props.item);
            }}
          >
            {props.item.DL_Disp}
          </Button>
        </TableCell>
      </TableRow>
    </>
  );
};

export default IoTBoxSettingGridItem;
